import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebaseConfig';
import {
  REVIEW_CONFIG_COLLECTION,
  REVIEW_CONFIG_DOC_ID,
  REVIEW_FEEDBACK_SUBCOLLECTION,
  REVIEW_FLOW_STORAGE_KEY,
  REVIEW_STATE_COLLECTION,
  TRUSTPILOT_FALLBACK_URL,
} from '../constants';
import type { ReviewPersistedState, ReviewSentiment } from '../types';

export type ReviewConfig = { reviewFlow: number; trustpilotUrl: string };

const DEFAULT_STATE: ReviewPersistedState = {
  completed: false,
  shownCount: 0,
  happyMomentCount: 0,
  lastShownAt: 0,
  lastSentiment: null,
};

export function readLocalState(): ReviewPersistedState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(REVIEW_FLOW_STORAGE_KEY);
    return raw ? { ...DEFAULT_STATE, ...JSON.parse(raw) } : null;
  } catch {
    return null;
  }
}

function writeLocalState(patch: Partial<ReviewPersistedState>): void {
  if (typeof window === 'undefined') return;
  try {
    const current = readLocalState() ?? DEFAULT_STATE;
    window.localStorage.setItem(
      REVIEW_FLOW_STORAGE_KEY,
      JSON.stringify({ ...current, ...patch })
    );
  } catch {}
}

function clearLocalState(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REVIEW_FLOW_STORAGE_KEY);
  } catch {}
}

export async function loadReviewConfig(): Promise<ReviewConfig> {
  try {
    const ref = doc(db, REVIEW_CONFIG_COLLECTION, REVIEW_CONFIG_DOC_ID);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      return {
        reviewFlow: Number(data.value ?? 0) || 0,
        trustpilotUrl:
          (data.trustpilotUrl as string) || TRUSTPILOT_FALLBACK_URL,
      };
    }
  } catch (error) {
    console.error('loadReviewConfig error:', error);
  }
  return { reviewFlow: 0, trustpilotUrl: TRUSTPILOT_FALLBACK_URL };
}

export async function loadReviewState(
  uid: string
): Promise<ReviewPersistedState> {
  try {
    const ref = doc(db, REVIEW_STATE_COLLECTION, uid);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const d = snap.data();
      const state: ReviewPersistedState = {
        completed: Boolean(d.completed),
        shownCount: Number(d.shownCount ?? 0) || 0,
        happyMomentCount: Number(d.happyMomentCount ?? 0) || 0,
        lastShownAt: Number(d.lastShownAt ?? 0) || 0,
        lastSentiment: (d.lastSentiment as ReviewSentiment) ?? null,
      };
      writeLocalState(state);
      return state;
    }
    clearLocalState();
    return DEFAULT_STATE;
  } catch (error) {
    console.error('loadReviewState error:', error);
    return readLocalState() ?? DEFAULT_STATE;
  }
}

async function mergeState(
  uid: string,
  patch: Record<string, unknown>
): Promise<void> {
  const ref = doc(db, REVIEW_STATE_COLLECTION, uid);
  await setDoc(ref, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}

export async function persistHappyMoment(
  uid: string,
  happyMomentCount: number
): Promise<void> {
  writeLocalState({ happyMomentCount });
  try {
    await mergeState(uid, { happyMomentCount });
  } catch (error) {
    console.error('persistHappyMoment error:', error);
  }
}

export async function persistShown(
  uid: string,
  shownCount: number,
  lastShownAt: number
): Promise<void> {
  writeLocalState({ shownCount, lastShownAt });
  try {
    await mergeState(uid, { shownCount, lastShownAt });
  } catch (error) {
    console.error('persistShown error:', error);
  }
}

export async function persistSentiment(
  uid: string,
  sentiment: ReviewSentiment
): Promise<void> {
  writeLocalState({ lastSentiment: sentiment });
  try {
    await mergeState(uid, { lastSentiment: sentiment });
  } catch (error) {
    console.error('persistSentiment error:', error);
  }
}

export async function persistCompleted(uid: string): Promise<void> {
  writeLocalState({ completed: true });
  try {
    await mergeState(uid, { completed: true });
  } catch (error) {
    console.error('persistCompleted error:', error);
  }
}

export async function saveInternalFeedback(
  uid: string,
  data: {
    sentiment: ReviewSentiment;
    feedbackText: string;
    featureName?: string | null;
  }
): Promise<void> {
  try {
    const col = collection(
      db,
      REVIEW_STATE_COLLECTION,
      uid,
      REVIEW_FEEDBACK_SUBCOLLECTION
    );
    await addDoc(col, {
      sentiment: data.sentiment,
      feedbackText: data.feedbackText,
      featureName: data.featureName ?? null,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('saveInternalFeedback error:', error);
  }
}
